/**
 * GeoService - Handles geo-location detection and proxy routing for blocked countries
 * 
 * The chat worker exposes a /api/geo endpoint that returns:
 * { country: string, blocked: boolean, proxyUrl: string|null }
 * 
 * Blocked countries (CN, HK, TW, RU, MO, KP, IR, SY, CU, BY, VE, VN) should route through
 * the proxy worker (wss://chat-proxy.languatalk.com) instead of direct connection.
 * 
 * Fresh geo check is performed on each chat connection - no caching.
 * This ensures accurate routing when users travel or use VPNs.
 * 
 * WebSocket Redirect Handling:
 * If a geo-blocking error occurs mid-stream (OpenAI/Anthropic 403 "unsupported_country_region_territory"),
 * the worker sends a geo_redirect message and the client automatically reconnects to the proxy.
 */

// Proxy worker URL for blocked countries
const PROXY_WORKER_URL = 'wss://chat-proxy.languatalk.com';

class GeoService {
  constructor() {
    this.geoStatus = null;
    this.checkPromise = null;
    this.geoCheckUrl = null; // Set dynamically based on workerUrl
  }

  /**
   * Derive geo check URL from worker URL
   * @param {string} workerUrl - WebSocket URL like wss://chat.languatalk.com
   * @returns {string} HTTPS URL for geo endpoint
   */
  deriveGeoCheckUrl(workerUrl) {
    // Convert wss:// to https:// and append /api/geo
    return workerUrl.replace('wss://', 'https://') + '/api/geo';
  }

  /**
   * Check geo status - fetches fresh from API each time
   * @param {string} workerUrl - The worker URL to derive geo check endpoint from
   * @returns {Promise<Object>} Geo status { country, blocked }
   */
  async checkGeoStatus(workerUrl) {
    // Set the geo check URL based on the worker URL (environment-aware)
    if (workerUrl) {
      this.geoCheckUrl = this.deriveGeoCheckUrl(workerUrl);
    }

    // If a check is already in progress, wait for it
    if (this.checkPromise) {
      return this.checkPromise;
    }

    // Fetch fresh from API
    this.checkPromise = this.fetchGeoStatus();
    
    try {
      const status = await this.checkPromise;
      this.geoStatus = status;
      return status;
    } finally {
      this.checkPromise = null;
    }
  }

  /**
   * Fetch geo status from the chat worker API
   * @returns {Promise<Object>} Geo status { country, blocked }
   */
  async fetchGeoStatus() {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);
    
    // Use environment-specific URL, fallback to production
    const geoUrl = this.geoCheckUrl || 'https://chat.languatalk.com/api/geo';
    
    try {
      const response = await fetch(geoUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        },
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(`Geo check failed: ${response.status}`);
      }

      const data = await response.json();
      return {
        country: data.country || 'unknown',
        blocked: data.blocked === true
      };
    } catch (error) {
      console.warn('Geo check failed, assuming not blocked:', error);
      // On error, assume not blocked (fail open)
      return { country: 'unknown', blocked: false };
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Check if the user is in a blocked country
   * @returns {boolean} True if blocked
   */
  isBlocked() {
    return this.geoStatus?.blocked === true;
  }

  /**
   * Get the appropriate worker URL based on geo status
   * @param {string} directUrl - The direct worker URL
   * @returns {string} Either the direct URL or proxy URL
   */
  getWorkerUrl(directUrl) {
    // Don't override environment-specific URLs (dev/staging)
    const isDevUrl = directUrl.includes('chat-worker-dev') || directUrl.includes('staging-chat');
    if (!isDevUrl && this.isBlocked()) {
      console.log(`Geo blocked (${this.geoStatus?.country}), routing through proxy`);
      return PROXY_WORKER_URL;
    }
    return directUrl;
  }
}

// Export singleton instance
const geoService = new GeoService();
export default geoService;
